import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Spin,
  Result,
  Divider,
  message,
  Typography,
  Space,
  Image,
  Row,
  Col
} from "antd";
import {
  HeartFilled,
  HeartOutlined,
  MessageOutlined,
  GlobalOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import ShareButton from './ShareButton.js';
import axios from 'axios';
import { API_LIKE_ENDPOINT, API_ENDPOINTS, formatBlogContent } from './config.js';
import './BlogDetail.css';
import CommentsSection from './CommentsSection.js';
import sh_img from '../images/sh_img.jpg';
import { Footer } from "./footer.js";
const { Title, Text } = Typography;

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [animateLike, setAnimateLike] = useState(false);
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang");
  const [isTelugu, setIsTelugu] = useState(lang === "telugu");
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_ENDPOINTS.GET_SINGLE_BLOG}&id=${id}`);
        if (!response.data) {
          throw new Error('Blog post not found');
        }
        setPost(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchLikeCount = async () => {
      try {
        const response = await axios.get(API_LIKE_ENDPOINT, {
          params: { blog_id: id }
        });
        setLikeCount(response.data.like_count || 0);
      } catch (error) {
        console.error("Error fetching like count:", error);
      }
    };
    fetchLikeCount();
  }, [id]);


  const handleLike = async () => {
    setLiked(true);
    setAnimateLike(true);
    setLikeCount(prev => prev + 1);
    try {
      const response = await axios.post(API_LIKE_ENDPOINT, { blog_id: id });
      if (response.data.like_count !== likeCount + 1) {
        setLikeCount(response.data.like_count);
      }
      message.success('Thanks for your like!');
    } catch (error) {
      console.error("Error submitting like:", error);
      setLiked(false);
      setLikeCount(prev => prev - 1);
      message.error('Failed to register like. Please try again.');
    } finally {
      setTimeout(() => setAnimateLike(false), 1000);
    }
  };

  const handleLanguageToggle = () => {
    const newLang = isTelugu ? "english" : "telugu";
    setIsTelugu(!isTelugu);
    message.info(`Switched to ${newLang} content`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading blog post..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Result
          status="404"
          title="Post Not Found"
          subTitle={`We couldn't find the blog post you're looking for (ID: ${id})`}
          extra={
            <Button type="primary" onClick={() => navigate('/blog')} icon={<ArrowLeftOutlined />}>
              Back to Blog
            </Button>
          }
        />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div>
      <div className="blog-detail-container">
        <Row justify="center">
          <Col xs={24} lg={18}>
            <div className="blog-content-wrapper">
              <Button
                type="text"
                onClick={() => navigate('/blog')}
                icon={<ArrowLeftOutlined />}
                className="back-button"
                style={{ marginLeft: '-20px' }}
              >
                Back
              </Button>

              {/* Blog Title + Author beside */}
              <div className="blog-header">
                <Space align="center" wrap>
                  <Title level={1} className="blog-title">
                    {formatBlogContent(post.blog_name)}
                  </Title>
                </Space>
              </div>

              {/* Blog Image */}
              <div className="blog-image-container">
                <Image
                  src={post.blog_image}
                  className="blog-image"
                  preview={false}
                />
              </div>

              {/* Language Toggle */}
              <div className="author-language-container">
                <Space align="center" className="author-inline">
                  <Image
                    src={sh_img} // Replace with actual avatar
                    alt="Shiny"
                    width={40}
                    height={40}
                    style={{ borderRadius: '50%' }}
                    preview={false}
                  />
                  <Text className="author-name">Dr Shiny Angel</Text>
                </Space>

                <Button
                  onClick={handleLanguageToggle}
                  type="text"
                  icon={<GlobalOutlined />}
                  className="language-toggle-button"
                >
                  {isTelugu ? 'English' : 'తెలుగు'}
                </Button>
              </div>



              {/* Blog Content */}
              <div
                className={`blog-content ${isTelugu ? 'telugu-font' : ''}`}
                dangerouslySetInnerHTML={{
                  __html: isTelugu ? formatBlogContent(post.blog_content_telugu) : formatBlogContent(post.blog_content_english),
                }}
              />

              <Divider />

              {/* Actions */}
              <div className="action-buttons">
                    <Button
                      className={`like-button ${liked ? 'liked' : ''} ${animateLike ? 'animate' : ''}`}
                      type="text"
                      icon={liked ? <HeartFilled /> : <HeartOutlined />}
                      onClick={handleLike}
                      disabled={liked}
                    >
                      {likeCount} {liked ? 'Liked' : 'Likes'}
                    </Button>
                    <Button
                      type="text"
                      icon={<MessageOutlined />}
                      onClick={() => setShowComments(!showComments)}
                    >Comments
                    </Button>

                    <ShareButton
                      url={window.location.href}
                      title={post.blog_name}
                      quote={`Check out this blog: ${post.blog_name}`}
                    />
              </div>
              {showComments && (
                <div className="comments-section">
                  <CommentsSection blogId={id} />
                </div>
              )}
            </div>
          </Col>
        </Row>


      </div>
      <Footer /></div>
  );
};

export default BlogDetail;
